import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { formatNumber } from 'helpers/getNumberFormats';
import { useToggle } from "hooks/useToggle";
import dayjs from 'dayjs';
import { styleIcon } from "./styles"
import { Add, Delete, Edit, Visibility } from "@mui/icons-material";
import { ModalV2 } from "components/Modal/ModalV2";
import { FormInventoryStatus } from "./FormInventoryStatusEdit";
import { PercentChangesHistory } from "./PercentChangesHistory";
import { deleteInventoryStatus } from "redux/actions/inventoryStatus/inventoryStatusActions";
import {  Autocomplete, TextField, Tooltip } from '@mui/material';
import RegularButton from "components/CustomButtons/Button";
import { useRuleValidator } from "hooks/useRuleValidator";
import { PERMISSIONS } from "router/permissions";

const WaitingActionsCell = ({ original, date }) => {
    const [isOpenCreate, handleIsOpenCreate] = useToggle();
    const canCreate = !original?.idStorage &&
        dayjs(date).isValid() && dayjs(date).isSame(dayjs(), 'day');
    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            {canCreate ? <Add style={styleIcon} onClick={handleIsOpenCreate} /> : null}
            <ModalV2
                open={isOpenCreate}
                onClose={handleIsOpenCreate}
                title={`Crear Inventario: ${original?.tank}`}
                toRight
            >
                <FormInventoryStatus handleClose={handleIsOpenCreate} data={original} />
            </ModalV2>
        </div>
    );
};

const ClosedActionsCell = ({ original, date, canModify, ruleDelete }) => {
    const { id, unit, idUnit } = original;
    const dispatch = useDispatch();
    const { reasonsOptions, station, unitType } = useSelector(({ inventoryStatus }) => inventoryStatus);
    const [isOpenEdit, handleIsOpenEdit] = useToggle();
    const [isOpenDelete, handleIsOpenDelete] = useToggle();
    const [isOpenHistory, handleIsOpenHistory] = useToggle();
    const [reason, setReason] = useState(null);
    const canViewHistory = useRuleValidator(PERMISSIONS.INVENTORIES_HISTORY);
    const isToday = dayjs(date).isValid() && dayjs(date).isSame(dayjs(), 'day');
    const handleDelete = () => {
        dispatch(deleteInventoryStatus(id, reason?.id, unit, idUnit, station?.id, date, unitType, handleIsOpenDelete));
    };
    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            {canViewHistory && original?.percentChanges?.length > 0 ? (
                <Tooltip title="Ver historial de cambios">
                    <Visibility style={styleIcon} onClick={handleIsOpenHistory} />
                </Tooltip>
            ) : null}

            {canModify && !original?.idStorage && isToday ? (
                <Edit style={styleIcon} onClick={handleIsOpenEdit} />
            ) : null}

            {canModify && ruleDelete && !original?.idStorage && isToday ? (
                <Delete style={{ ...styleIcon, color: '#e83737' }} onClick={handleIsOpenDelete} />
            ) : null}

            <ModalV2
                open={isOpenHistory}
                onClose={handleIsOpenHistory}
                title={`Historial de cambios: ${original?.tank}`}
                toRight
            >
                <PercentChangesHistory data={original} />
            </ModalV2>

            <ModalV2
                open={isOpenEdit}
                onClose={handleIsOpenEdit}
                title={`Editar Inventario: ${original?.tank}`}
                toRight
            >
                <FormInventoryStatus handleClose={handleIsOpenEdit} data={original} isEdit />
            </ModalV2>

            <ModalV2
                open={isOpenDelete}
                onClose={() => {
                    handleIsOpenDelete()
                    setReason(null)
                }}
                justConfirm
                title={`Esta seguro que desea eliminar de unidades cerradas?`}
            >
                <Autocomplete
                    id="combo-box-demo"
                    options={reasonsOptions.filter(r => r.application === "REMOVE")}
                    value={reason}
                    filterSelectedOptions
                    getOptionLabel={(option) => option.label ? option.label : option.name}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            name="type"
                            label="Razón *"
                            variant="outlined"
                        />
                    )}
                    fullWidth
                    onChange={(_e, v) => setReason(v)}
                    noOptionsText="No se encuentran razones"
                />
                <div style={{
                    marginTop: 14,
                    width: '100%',
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8
                }}>
                    <RegularButton
                        color="info"
                        onClick={handleIsOpenDelete}
                    >
                        Cancelar
                    </RegularButton>
                    <RegularButton
                        color="danger"
                        onClick={handleDelete}
                        disabled={!reason?.id}
                    >
                        Confirmar
                    </RegularButton>
                </div>
            </ModalV2>
        </div>
    );
};

export const useWaitingClose = (
    showActions,
    isInventoryClosed,
    date,
    inventoryPercentage,
    inventoryComplete
) => {
    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Unidad',
                accessor: "unit",
                filterable: true,
                filterMethod: (filter, row) => row.unit?.toLowerCase().includes(filter.value.toLowerCase()),
            },
            {
                Header: 'Tanque',
                accessor: "tank",
                filterable: true,
                filterMethod: (filter, row) => row.tank?.toLowerCase().includes(filter.value.toLowerCase()),
            },
            {
                Header: 'Fecha ultima sincronización',
                accessor: "lastInventory",
                filterable: true,
                filterMethod: (filter, row) => row.lastInventory?.toLowerCase().includes(filter.value.toLowerCase()),
                Cell: ({ original }) => (<div style={{ float: 'right', marginRight: 16 }}>{original.lastInventory?.replace(/\.0+$/, '')}</div>)
            },
        ]
        if (showActions && !isInventoryClosed) {
            cols.push({
                Header: "Acciones",
                filterable: false,
                sortable: false,
                Cell: ({ original }) => <WaitingActionsCell original={original} date={date} />,
            },);
        }
        return cols;
    }, [showActions, isInventoryClosed, date, inventoryPercentage, inventoryComplete]);
    return columns;
}

export const useClosed = (
    showActions,
    isInventoryClosed,
    date,
    inventoryPercentage,
    inventoryComplete,
    ruleDelete
) => {
    const canViewHistory = useRuleValidator(PERMISSIONS.INVENTORIES_HISTORY);
    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Unidad',
                accessor: "unit",
                filterable: true,
                filterMethod: (filter, row) => row.unit?.toLowerCase().includes(filter.value.toLowerCase()),
            },
            {
                Header: 'Tanque',
                accessor: "tank",
                filterable: true,
                filterMethod: (filter, row) => row.tank?.toLowerCase().includes(filter.value.toLowerCase()),
            },
            {
                Header: '%',
                accessor: "percentage",
                filterable: true,
                Cell: ({ original }) => `${formatNumber(original?.percentage)}%`
            },
            {
                Header: 'Fecha de cierre',
                accessor: "closureDate",
                filterable: true,
                filterMethod: (filter, row) => row.closureDate?.toLowerCase().includes(filter.value.toLowerCase()),
                Cell: ({ original }) => `${original?.closureDate?.replace(/\.0+$/, '')}`
            },
            {
                Header: 'Usuario',
                accessor: "username",
                filterable: true,
                filterMethod: (filter, row) => row.username?.toLowerCase().includes(filter.value.toLowerCase()),
            },
            {
                Header: 'Medio',
                accessor: "origin",
                filterable: true,
                filterMethod: (filter, row) => row.origin?.toLowerCase().includes(filter.value.toLowerCase()),
                Cell: ({ original }) => (<div style={{ float: 'right', marginRight: 16 }}>{original.origin}</div>)
            },
        ]
        const hasHistory = inventoryComplete?.some((item) => item?.percentChanges?.length > 0);
        const canModify = showActions && !isInventoryClosed;
        if (canModify || (canViewHistory && hasHistory)) {
            cols.push({
                Header: "Acciones",
                filterable: false,
                sortable: false,
                Cell: ({ original }) => (
                    <ClosedActionsCell
                        original={original}
                        date={date}
                        canModify={canModify}
                        ruleDelete={ruleDelete}
                    />
                ),
            },);
        }
        return cols;
    }, [showActions, isInventoryClosed, date, inventoryPercentage, inventoryComplete, ruleDelete, canViewHistory]);
    return columns;
}
